import { type UseFormReturn, type FieldValues, type FieldPath } from 'react-hook-form';

import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';

type MultiSelectFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label: string;
    options?: { id: string; name: string }[];
    required?: boolean;
};

export default function MultiSelectFormField<TData extends FieldValues>({
    name,
    label,
    options,
    form,
    required = true,
}: MultiSelectFormFieldProps<TData>) {
    if (!options) return null;
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const allValues = options?.map((option) => option.id);

                const fieldValue = Array.isArray(field.value) ? field.value : [];
                const fieldData = fieldValue
                    .map((value) => options?.find((option) => option.id === value)?.name)
                    .join(', ');

                const MultiSelectFormField = {
                    ...field,
                    value: fieldValue,
                };

                return (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required={required}>
                            {label}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <MultiSelect {...MultiSelectFormField}>
                                    <MultiSelectTrigger asChild>
                                        <MultiSelectControl>
                                            {fieldData}
                                            {fieldData.length == 0 && <span className="text-gray-400">{label}...</span>}
                                        </MultiSelectControl>
                                    </MultiSelectTrigger>
                                    <MultiSelectContent allValues={allValues}>
                                        {options?.map((user) => (
                                            <MultiSelectOption key={user.id} selected={user.id}>
                                                {user.name}
                                            </MultiSelectOption>
                                        ))}
                                    </MultiSelectContent>
                                </MultiSelect>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}
